import { memo } from "react";
import { AssetIcon } from "@/components";
import { Button, Dialog, DialogContent } from "../../shared";
import { useDialogPortal, useLocalStorage } from "@/hooks";
import { THEME_STORAGE_KEY } from "@/config";
import ga4 from "react-ga4";
import posthog from "posthog-js";
import { PostHogEvent } from "@/posthog/posthog-capture-names";
import { useUserTvl } from "@/hooks/infinifi/useUserTvl";
import { useTrackingConsent } from "@/hooks/shared/tracking-consent/useTrackingConsent";
import { safaryTrackWhaleDialogTelegramLinkClick } from "@/services/analytics/safary.service";
import { useAccount } from "wagmi";
import { LiaTelegram } from "react-icons/lia";

const WHALE_RETENTION_DIALOG_CONTACT = {
    name: "Valeria (Val) Pereira",
    telegram: {
        href: "https://t.me/valeriapereira_infini", // Update with actual
        label: "Contact us via Telegram",
    },
};

const handleClose = (_event: any, reason: string, userTotalTvl: number, closeDialog: () => void) => {
    if (reason === "backdropClick") return;
    closeDialog();
    // tracking...
    try {
        ga4.event("whale_retention_dialog_closed", { value: userTotalTvl });
    } catch {}
    try {
        posthog.capture(PostHogEvent.WhaleRetentionDialog.closed, { value: userTotalTvl });
    } catch {}
};

const handleTelegramClick = ({
    userTotalTvl,
    address,
    canTrack,
}: {
    userTotalTvl: number;
    address?: string;
    canTrack: boolean;
}) => {
    window.open(WHALE_RETENTION_DIALOG_CONTACT.telegram.href, "_blank");
    // tracking...
    try {
        canTrack && ga4.event("whale_retention_dialog_telegram_clicked", { value: userTotalTvl });
    } catch {}
    try {
        canTrack && posthog.capture(PostHogEvent.WhaleRetentionDialog.TelegramButton.clicked, { value: userTotalTvl });
    } catch {}
    try {
        canTrack &&
            address &&
            safaryTrackWhaleDialogTelegramLinkClick({ userTvl: userTotalTvl, whaleAddress: address });
    } catch {}
};

const WhaleBadge = memo(() => (
    <div className="bg-primary/5 border-4 border-primary/20 rounded-full flex items-center justify-center aspect-square w-[100px]">
        <AssetIcon name="INFINIFI_X" size={72} className="ml-2" />
    </div>
));

const PowerUserSection = memo(() => (
    <div className="space-y-1">
        <p className="text-sm leading-tight text-muted font-medium">
            Based on your activity, <br></br>you are considered an
        </p>
        <p
            className="text-xl font-[500] bg-clip-text text-transparent"
            style={{
                backgroundImage:
                    "linear-gradient(90deg, #005E5D 0%, #1A7271 25%, #2A9D8F 50%, #1A7271 75%, #005E5D 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
            }}
        >
            infiniFi power user!
        </p>
    </div>
));

const SupportText = memo(() => (
    <p className="text-sm leading-tight text-muted flex flex-col">
        <span>We'd love to offer you personalized support</span>
        <span className="font-medium text-primary">with a dedicated team contact.</span>
    </p>
));

const ContactHeader = memo(() => (
    <div className="flex items-center space-x-4">
        <div>
            <p className="text-base font-semibold ">
                <span className="text-primary">{WHALE_RETENTION_DIALOG_CONTACT.name}</span> will be your direct line
                for:
            </p>
        </div>
    </div>
));

const BenefitItem = memo(({ text }: { text: string }) => (
    <li className="flex items-start space-x-3 relative marker:text-primary">
        <span className="text-sm text-dark leading-relaxed">{text}</span>
    </li>
));

const BenefitsList = memo(() => (
    <ul className="list-disc pl-5 space-y-3">
        <BenefitItem text="Priority technical support" />
        <BenefitItem text="Strategy alpha & feature sneak peeks" />
        <BenefitItem text="Real-time protocol updates" />
        <BenefitItem text="Or a nice 1:1 contact to chat about DeFi" />
    </ul>
));

const BlurSeperator = memo(() => (
    <div
        style={{
            width: 84,
            height: 1.64,
            position: "relative",
            background:
                "linear-gradient(90deg, rgba(0, 0, 0, 0) 0%, rgba(26, 114, 113, 0.50) 50%, rgba(0, 0, 0, 0) 100%)",
        }}
    />
));

const PrivacySection = memo(() => (
    <div className="space-y-3 pt-4 border-t border-muted">
        <p className="text-[14px] leading-[23.2px] text-muted">
            You can stay fully anon. The team will never ask you for personal details or to dox yourself.
        </p>
        <p className="text-[14px] leading-[23.2px] text-muted">
            Just drop a <span className="font-semibold">Telegram message</span> or get in touch with a core team member.
        </p>
    </div>
));

export const WhaleRetentionDialog = memo(() => {
    const { closeDialog } = useDialogPortal();
    const { userTotalTvl, isLoading: isUserTvlLoading } = useUserTvl();
    const { canTrack } = useTrackingConsent();
    const { address } = useAccount();
    const [persistedTheme] = useLocalStorage<"light" | "dark">(THEME_STORAGE_KEY, "light");
    const resolvedTheme = persistedTheme === "dark" ? "dark" : "light";

    if (isUserTvlLoading) {
        return null;
    }

    return (
        <Dialog
            data-theme={resolvedTheme}
            open
            onClose={(e) => handleClose(e, "closeButton", userTotalTvl, closeDialog)}
            PaperProps={{
                className: "max-w-4xl rounded-3xl shadow-2xl overflow-hidden",
                "data-theme": resolvedTheme,
            }}
        >
            {/* <div className="relative rounded-3xl p-1 bg-gradient-to-r from-primary/30 via-primary to-primary/30 shadow-[0_20px_60px_rgba(26,114,113,0.3)]"> */}
            <DialogContent className="m-0 p-0 py-4 rounded-3xl max-h-screen bg-light/20">
                {/* Gradient overlay */}
                <div className="absolute inset-0 pointer-events-none" />
                {/* Bottom gradient line */}
                <div className="absolute bottom-0 left-0 right-0 h-1" />

                <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 px-8 pb-8 pt-4">
                    {/* Left */}
                    <div className="flex flex-col items-center text-center max-w-xs space-y-4">
                        <WhaleBadge />
                        <PowerUserSection />
                        <BlurSeperator />
                        <SupportText />
                        <Button
                            variant="primary"
                            size="md"
                            fullWidth
                            onClick={() => handleTelegramClick({ userTotalTvl, address, canTrack })}
                        >
                            Contact us via Telegram
                            <LiaTelegram className="ml-2 w-5 h-5" />
                        </Button>
                    </div>

                    {/* Right */}
                    <div className="flex flex-col w-80 space-y-5">
                        <ContactHeader />
                        <BenefitsList />
                        <PrivacySection />
                    </div>
                </div>
            </DialogContent>
            {/* </div> */}
        </Dialog>
    );
});

WhaleBadge.displayName = "WhaleBadge";

PowerUserSection.displayName = "PowerUserSection";

SupportText.displayName = "SupportText";

ContactHeader.displayName = "ContactHeader";

BenefitItem.displayName = "BenefitItem";

BenefitsList.displayName = "BenefitsList";

BlurSeperator.displayName = "BlurSeperator";

PrivacySection.displayName = "PrivacySection";

WhaleRetentionDialog.displayName = "WhaleRetentionDialog";
